<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { t } = useI18n();

// Props
const props = defineProps({
    modal: {
        type: Object,
        default: () => ({})
    }
});

// Emits
const emit = defineEmits(['close', 'save']);

// Refs
const searchInput = ref(null);
const searchQuery = ref(props.modal.initialQuery || '');
const searchResults = ref(null);
const loading = ref(false);
const linking = ref(false);
const linkError = ref('');
const selectedResult = ref(null);

// Computed
const parsedUrl = computed(() => {
    const q = searchQuery.value.trim();
    // Match /person/... or /studio/... with optional full domain
    const match = q.match(/(?:https?:\/\/[^/]+)?\/?(person|studio)\/(.+)/);
    if (match) {
        return { type: match[1], id: match[2] };
    }
    return null;
});

const isUrl = computed(() => !!parsedUrl.value);

// Methods
const close = () => {
    emit('close');
};

const escapeListener = (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
        close();
    }
};

let _searchTimeout = null;
const onInput = () => {
    searchResults.value = null;
    linkError.value = '';
    selectedResult.value = null;

    if (isUrl.value) return;

    const q = searchQuery.value.trim();
    if (q.length < 2) return;

    clearTimeout(_searchTimeout);
    _searchTimeout = setTimeout(async () => {
        if (q !== searchQuery.value.trim()) return;

        loading.value = true;
        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/search/?q=${encodeURIComponent(q)}&type=staff`);
            if (res && res.ok) {
                const data = await res.json();
                searchResults.value = data.staff || [];
            } else {
                searchResults.value = [];
            }
        } catch (e) {
            searchResults.value = [];
        }
        loading.value = false;
    }, 250);
};

const getResultId = (result) => {
    if (result.anilist_id) return result.anilist_id;
    if (result.ja) return `ja:${encodeURIComponent(result.ja).replace(/%2F/g, '%252F')}`;
    if (result.en) return `en:${encodeURIComponent(result.en).replace(/%2F/g, '%252F')}`;
    return null;
};

const getStudios = (staff) => {
    const studios = staff?.studios;
    if (!studios || studios.length === 0) {
        return null;
    }

    const keys = Object.keys(studios);
    const seen = new Set();
    return keys.filter(key => {
        const lower = key.toLowerCase();
        if (seen.has(lower)) {
            return false;
        }
        seen.add(lower);
        return true;
    }).join(', ');
};

const getJobs = (staff) => {
    const jobs = staff?.jobs;
    if (!jobs || jobs.length === 0) {
        return null;
    }
    return jobs.join(', ');
};

const getImage = (staff) => {
    if (staff?.avatar && staff?.anilist_id) {
        const prefix = staff.is_studio === 1 ? '0' : '1';
        return `/assets/img/staff/${prefix}-${staff.anilist_id}/${staff.avatar}.jpg`;
    }
    return null;
};

const fetchAndApply = async (id, type) => {
    linking.value = true;
    linkError.value = '';
    try {
        const encodedId = /^(ja|en):/.test(id)
            ? `${id.slice(0, 3)}${decodeURIComponent(id.slice(3)).replace(/[+]/g, '%2B').replace(/&/g, '%26')}`
            : decodeURIComponent(id).replace(/[+]/g, '%2B').replace(/&/g, '%26');
        const res = await fetch(`https://keyframe-staff-list.com/api/person/show.php?id=${encodedId}&type=${type}`);
        if (!res.ok) throw new Error('request_failed');
        const data = await res.json();
        if (!data || !data.staff) throw new Error('invalid_response');

        data.staff.isStudio = type === 'studio' ? 1 : 0;
        emit('save', data.staff);
        close();
    } catch (e) {
        linkError.value = t('staffComponent.messages.linkToPageError');
    }
    linking.value = false;
};

const selectResult = (result) => {
    const currentId = selectedResult.value ? getResultId(selectedResult.value) : null;
    const nextId = getResultId(result);
    const currentType = selectedResult.value?.is_studio === 1 ? 'studio' : 'person';
    const nextType = result.is_studio === 1 ? 'studio' : 'person';

    if (currentId && nextId && currentId === nextId && currentType === nextType) {
        selectedResult.value = null;
        return;
    }

    selectedResult.value = result;
    linkError.value = '';
};

const confirmSelected = () => {
    if (!selectedResult.value) return;
    const id = getResultId(selectedResult.value);
    if (!id) return;
    const type = selectedResult.value.is_studio === 1 ? 'studio' : 'person';
    fetchAndApply(id, type);
};

const getResultKey = (result) => {
    const id = getResultId(result);
    if (!id) return null;
    const type = result.is_studio === 1 ? 'studio' : 'person';
    return `${type}:${id}`;
};

const isSelected = (result) => {
    if (!selectedResult.value) return false;
    return getResultKey(result) && getResultKey(result) === getResultKey(selectedResult.value);
};

const unlink = () => {
    emit('save', -1);
    close();
};

const onEnter = () => {
    if (!isUrl.value) return;
    const { id, type } = parsedUrl.value;
    fetchAndApply(id, type);
};

// Lifecycle
onMounted(() => {
    document.addEventListener('keydown', escapeListener);
    nextTick(() => {
        if (searchInput.value) searchInput.value.focus();
        if (searchQuery.value) onInput();
    });
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', escapeListener);
    clearTimeout(_searchTimeout);
});
</script>

<template>
    <!-- Fond -->
    <div
        class="fixed top-0 left-0 w-full h-full bg-black/50 flex-center p-[calc(min(5rem,10%))] overflow-y-auto"
        :style="{ zIndex: modal.zIndex || 50 }"
    >
        <!-- Dialog -->
        <Transition appear enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100">
            <div
                class="dialog bg-app-surface1 rounded-xl overflow-hidden w-full max-w-[40em] shadow-2xl max-h-[90vh] overflow-y-auto transition-all duration-100 ease-out flex flex-col"
                role="dialog"
                aria-modal="true"
                :aria-label="t('staffComponent.messages.linkToPageTitle')"
            >
            <!-- Header avec titre et X -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-app-border">
                <h2 class="text-lg font-semibold text-app-text">{{ t('staffComponent.messages.linkToPageTitle') }}</h2>
                <button
                    type="button"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full text-app-text-soft hover:bg-app-border transition-colors"
                    @click="close"
                    :aria-label="t('common.close')"
                    :title="t('common.close')"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Contenu -->
            <div class="p-6 flex flex-col gap-4">
                <p class="text-sm text-app-text-soft">{{ t('staffComponent.messages.linkToPageHint') }}</p>

                <div class="relative">
                    <input
                        ref="searchInput"
                        type="search"
                        class="w-full input !pr-10"
                        :placeholder="t('staffComponent.messages.linkToPagePlaceholder')"
                        v-model="searchQuery"
                        @input="onInput"
                        @keydown.enter="onEnter"
                    />
                    <span v-if="isUrl" class="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-sm">
                        <font-awesome-icon icon="fa-solid fa-link" />
                    </span>
                </div>

                <!-- Search results -->
                <div v-if="!isUrl && loading" class="flex justify-center items-center py-4 text-slate-400">
                    <font-awesome-icon icon="fa-solid fa-circle-notch" spin class="text-2xl" />
                </div>
                <div v-else-if="!isUrl && searchResults && searchResults.length === 0" class="text-sm text-center text-slate-400 py-2">
                    {{ t('staffComponent.messages.linkToPageNoResults') }}
                </div>
                <div v-else-if="!isUrl && searchResults && searchResults.length" class="bg-white dark:bg-app-surface0 rounded-md border border-slate-200 dark:border-app-border overflow-y-auto max-h-80">
                    <ul class="divide-y divide-slate-100 dark:divide-app-border !m-0">
                        <li
                            v-for="staff in searchResults"
                            :key="staff.anilist_id || staff.ja || staff.en"
                            class="px-4 py-2 cursor-pointer flex flex-col sm:flex-row md:items-center gap-3"
                            :class="{
                                'font-bold': staff.is_studio === 1,
                                'hover:bg-slate-100 dark:hover:bg-app-surface2': !isSelected(staff),
                                'group is-active bg-ocean-400 hover:bg-ocean-400 text-white': isSelected(staff)
                            }"
                            @click="selectResult(staff)"
                        >
                            <img v-if="getImage(staff)" :src="getImage(staff)" alt="Avatar" class="inline-block w-12 h-12 object-cover mx-auto xs:m-0 rounded-md flex-shrink-0">
                            <div class="flex flex-col min-w-0 flex-1">
                                <div class="flex flex-col md:flex-row md:items-center md:gap-2 min-w-0">
                                    <div class="flex items-center gap-2 min-w-0">
                                        <font-awesome-icon v-if="staff.is_studio === 1" icon="fa-solid fa-briefcase" class="text-slate-400 dark:text-app-text-soft text-xs flex-shrink-0" />
                                        <span v-if="staff.en" class="truncate">{{ staff.en }}</span>
                                    </div>
                                    <span v-if="staff.ja" class="flex gap-1 flex-shrink-0">
                                        <span class="text-slate-400 dark:text-app-text-soft">(</span>{{ staff.ja }}<span class="text-slate-400 dark:text-app-text-soft">)</span>
                                    </span>
                                </div>
                                <div v-if="getStudios(staff) && staff.is_studio !== 1" class="text-xs italic text-gray-400 dark:text-app-text-soft group-[.is-active]:text-gray-200 leading-tight mt-1 truncate" :title="getStudios(staff)">
                                    ({{ getStudios(staff) }})
                                </div>
                                <div v-if="getJobs(staff)" class="mt-1 text-xs text-gray-500 dark:text-app-text-soft group-[.is-active]:text-gray-300 leading-tight font-normal truncate" :title="getJobs(staff)">
                                    <strong>Jobs:</strong> {{ getJobs(staff) }}
                                </div>
                            </div>
                            <div class="ml-auto flex items-center">
                                <a
                                    v-if="getResultId(staff)"
                                    :href="`/${staff.is_studio === 1 ? 'studio' : 'person'}/${getResultId(staff)}`"
                                    target="_blank"
                                    class="flex items-center justify-center w-8 h-8 text-lg"
                                    :class="isSelected(staff) ? 'text-white hover:text-white' : 'text-ocean-400 hover:text-ocean-500'"
                                    @click.stop
                                >
                                    <font-awesome-icon icon="fa-solid fa-arrow-up-right-from-square" />
                                </a>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Error -->
                <p v-if="linkError" class="text-sm text-red-500">{{ linkError }}</p>
            </div>

            <!-- Footer -->
            <div class="flex gap-2 pt-3 px-6 py-6 justify-end">
                <div class="flex-1">
                    <button type="button" class="btn btn-cancel !rounded-full" @click="close">
                        {{ t('common.cancel') }}
                    </button>
                </div>
                <button type="button" class="btn btn-danger !rounded-full" @click="unlink">
                    <font-awesome-icon icon="fa-solid fa-unlink" />
                    Unlink
                </button>
                <button
                    type="button"
                    class="btn btn-primary !rounded-full flex items-center justify-center gap-2"
                    :disabled="linking || (!isUrl && !selectedResult)"
                    @click="isUrl ? onEnter() : confirmSelected()"
                >
                    <font-awesome-icon v-if="linking" icon="fa-solid fa-circle-notch" spin />
                    <font-awesome-icon v-else icon="fa-solid fa-link" />
                    Link
                </button>
            </div>
            </div>
        </Transition>
    </div>
</template>
